import { useEffect, useState } from "react";
import { Home, Briefcase, Star, MapPin, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";

const ICON_MAP = { home: Home, work: Briefcase, star: Star, pin: MapPin };

export default function FavoritesPicker({ onPick, currentLat, currentLng, currentAddress }) {
  const [favs, setFavs] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [label, setLabel] = useState("");
  const [icon, setIcon] = useState("pin");

  const load = async () => {
    try { const res = await api.get("/chauffeur/favorites"); setFavs(res.data || []); }
    catch { /* anon viewer */ }
  };
  useEffect(() => { load(); }, []);

  const addCurrent = async () => {
    if (!currentAddress || !currentLat) { toast.error("Set a pin or address first"); return; }
    if (!label) { toast.error("Add a label"); return; }
    try {
      const res = await api.post("/chauffeur/favorites", {
        label, address: currentAddress, lat: currentLat, lng: currentLng, icon,
      });
      setFavs((s) => [...s, res.data]);
      setLabel(""); setShowAdd(false);
      toast.success(`Saved ${res.data.label}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };
  const removeFav = async (id) => {
    try {
      await api.delete(`/chauffeur/favorites/${id}`);
      setFavs((s) => s.filter((f) => f.id !== id));
    } catch { toast.error("Remove failed"); }
  };

  return (
    <div data-testid="favorites-picker">
      <div className="flex items-center gap-2 flex-wrap">
        {favs.map((f) => {
          const Icon = ICON_MAP[f.icon] || MapPin;
          return (
            <button key={f.id} data-testid={`fav-${f.id}`}
              onClick={() => onPick({ lat: f.lat, lng: f.lng, address: f.address })}
              className="group relative inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-body bg-white/5 border border-white/10 text-white/70 hover:text-gold hover:border-gold/30 transition-colors">
              <Icon size={12} className="text-gold" /> {f.label}
              <button onClick={(e) => { e.stopPropagation(); removeFav(f.id); }}
                data-testid={`fav-remove-${f.id}`}
                className="opacity-0 group-hover:opacity-100 ml-1 text-red-400/70 hover:text-red-400">
                <Trash2 size={10} />
              </button>
            </button>
          );
        })}
        {!showAdd && favs.length < 20 && (
          <button data-testid="fav-add-btn" onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-body border border-dashed border-white/20 text-white/50 hover:text-gold hover:border-gold/30 transition-colors">
            <Plus size={10} /> Save current
          </button>
        )}
      </div>
      {showAdd && (
        <div className="mt-2 flex items-center gap-2">
          <select data-testid="fav-icon-select" value={icon} onChange={(e) => setIcon(e.target.value)}
            className="bg-white/5 border border-white/15 text-white text-xs px-2 py-2">
            <option value="home">Home</option>
            <option value="work">Work</option>
            <option value="star">Favorite</option>
            <option value="pin">Place</option>
          </select>
          <Input data-testid="fav-label-input" value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="Label" className="bg-white/5 border-white/15 text-white text-xs flex-1" />
          <button data-testid="fav-save-btn" onClick={addCurrent}
            className="bg-gold text-luxury-black px-3 py-2 text-xs font-body uppercase tracking-wider hover:bg-gold/90 transition-colors">
            Save
          </button>
          <button onClick={() => setShowAdd(false)} className="text-white/40 hover:text-white px-1">×</button>
        </div>
      )}
    </div>
  );
}
